import {
  AbsoluteCenter,
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import { getAllCustomersApi } from 'api/adminApi';
import { getCustomerPipelineApi } from 'api/customerCockpitProfileApi';
import {
  addAccountingInvoiceSetOffApi,
  getAccountingInvoiceApi,
  listAccountingInvoicesApi,
} from 'api/accountingInternalApi';
import { getDealsApi } from 'api/dealApi';
import { getMarketingMonthlySpendDataApi } from 'api/marketingApi';
import ContentBox from 'components/ContentBox';
import Card from 'components/card/Card';
import SelectInput from 'components/inputs/SelectInput';
import BackLink from 'components/links/BackLink';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { downloadInvoicePdf } from './calc/downloadInvoicePdf';
import { filterLiveCustomers } from './calc/liveCustomers';
import {
  createGrowthInvestmentLine,
  getPreviousMonthKey,
  nextGrowthInvestmentLine,
  persistedGrowthInvestments,
  sumGrowthInvestments,
} from './calc/growthInvestments';
import { resolveMarketingSpendForMonth } from './calc/marketingSpend';
import { marketingViewIdForFunding, pickLatestDeal } from './calc/pickLatestDeal';
import { isGrowthInvestmentComplete, suggestGrowthAmount } from './calc/suggestGrowthAmount';
import InvoicePreview from './components/InvoicePreview';
import SetOffFundingPanel from './components/SetOffFundingPanel';

const CreateSetOff = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const setOffCreatedDate = useMemo(() => moment().format('YYYY-MM-DD'), []);

  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [latestDeal, setLatestDeal] = useState(null);
  const [growthInvestments, setGrowthInvestments] = useState(() => [
    createGrowthInvestmentLine({ fundingMonthKey: getPreviousMonthKey() }),
  ]);
  const [loadingLineIds, setLoadingLineIds] = useState({});
  const [growthInvestmentTouched, setGrowthInvestmentTouched] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [savingSetOff, setSavingSetOff] = useState(false);

  useEffect(() => {
    Promise.all([getAllCustomersApi(), getCustomerPipelineApi()])
      .then(([items, pipelineReport]) => {
        setCustomers(
          filterLiveCustomers(items, pipelineReport)
            .map((customer) => ({
              label: `${customer.companyName} (${customer.externalId})`,
              value: customer.id,
              customer,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        );
      })
      .catch(() => {
        toast({
          title: 'Error',
          description: 'Failed to load customers',
          status: 'error',
        });
      });
  }, [toast]);

  const loadMarketingSpend = async ({ customerId, monthKey, deal, lineId }) => {
    if (!customerId || !monthKey || !lineId) {
      return;
    }
    setLoadingLineIds((current) => ({ ...current, [lineId]: true }));
    try {
      const spendData = await getMarketingMonthlySpendDataApi({
        customerId,
        date: moment(`${monthKey}-01`).format('YYYY-MM-DD'),
        marketingViewId: marketingViewIdForFunding(deal),
      }).catch(() => []);
      const spend = resolveMarketingSpendForMonth(spendData, monthKey);
      const suggestion = suggestGrowthAmount({
        marketingSpend: spend,
        fundingPercentage: deal?.fundingPercentage,
      });
      setGrowthInvestments((current) =>
        current.map((line) =>
          line.id === lineId && line.fundingMonthKey === monthKey
            ? {
                ...line,
                marketingSpend: spend,
                growthInvestment: suggestion.suggestedAmount,
              }
            : line
        )
      );
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load marketing spend',
        status: 'error',
      });
    } finally {
      setLoadingLineIds((current) => {
        const next = { ...current };
        delete next[lineId];
        return next;
      });
    }
  };

  const loadCustomerInvoices = async (customerId) => {
    setLoadingInvoices(true);
    setSelectedInvoiceId(null);
    setSelectedInvoice(null);
    setInvoiceOptions([]);
    try {
      const [invoices, deals] = await Promise.all([
        listAccountingInvoicesApi({ customerId, withoutSetOff: true }),
        getDealsApi({ customerId }).catch(() => []),
      ]);
      const deal = pickLatestDeal(deals);
      setLatestDeal(deal);
      setInvoiceOptions(
        (invoices || []).map((invoice) => ({
          label: `${invoice.invoiceNumber} (${moment(invoice.invoiceDate).format('MM/DD/YYYY')})`,
          value: invoice.id,
        }))
      );
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load invoices',
        status: 'error',
      });
    } finally {
      setLoadingInvoices(false);
    }
  };

  const onCustomerChange = (customerId) => {
    setSelectedCustomerId(customerId);
    setSelectedInvoiceId(null);
    setSelectedInvoice(null);
    setGrowthInvestments([
      createGrowthInvestmentLine({ fundingMonthKey: getPreviousMonthKey() }),
    ]);
    setGrowthInvestmentTouched(false);
    if (customerId) {
      loadCustomerInvoices(customerId);
    } else {
      setInvoiceOptions([]);
    }
  };

  const onInvoiceChange = async (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    if (!invoiceId) {
      setSelectedInvoice(null);
      return;
    }
    setLoadingInvoice(true);
    try {
      const invoice = await getAccountingInvoiceApi({ invoiceId });
      if (!invoice) {
        toast({
          title: 'Invoice not found',
          status: 'error',
        });
        setSelectedInvoiceId(null);
        return;
      }
      setSelectedInvoice(invoice);
      const nextFundingMonth = getPreviousMonthKey();
      const firstLine = createGrowthInvestmentLine({ fundingMonthKey: nextFundingMonth });
      setGrowthInvestments([firstLine]);
      await loadMarketingSpend({
        customerId: selectedCustomerId,
        monthKey: nextFundingMonth,
        deal: latestDeal,
        lineId: firstLine.id,
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load invoice',
        status: 'error',
      });
    } finally {
      setLoadingInvoice(false);
    }
  };

  const onGrowthInvestmentLineChange = (index, partial) => {
    const currentLine = growthInvestments[index];
    if (!currentLine) {
      return;
    }
    const nextLine = { ...currentLine, ...partial };
    setGrowthInvestments((current) =>
      current.map((line) => (line.id === currentLine.id ? nextLine : line))
    );
    if ('growthInvestment' in partial) {
      setGrowthInvestmentTouched(true);
    }
    if (
      partial.fundingMonthKey &&
      partial.fundingMonthKey !== currentLine.fundingMonthKey &&
      selectedCustomerId
    ) {
      loadMarketingSpend({
        customerId: selectedCustomerId,
        monthKey: partial.fundingMonthKey,
        deal: latestDeal,
        lineId: currentLine.id,
      });
    }
  };

  const onAddGrowthInvestment = () => {
    const nextLine = nextGrowthInvestmentLine(growthInvestments);
    setGrowthInvestments((current) => [...current, nextLine]);
    if (selectedCustomerId && nextLine.fundingMonthKey) {
      loadMarketingSpend({
        customerId: selectedCustomerId,
        monthKey: nextLine.fundingMonthKey,
        deal: latestDeal,
        lineId: nextLine.id,
      });
    }
  };

  const onRemoveGrowthInvestment = (index) => {
    setGrowthInvestments((current) => current.filter((_, lineIndex) => lineIndex !== index));
  };

  const validateSetOffFields = () => {
    if (growthInvestments.some((line) => !line.requestReceivedDate)) {
      toast({
        title: 'Missing request date',
        description: 'Enter the date the funding request was received.',
        status: 'warning',
      });
      return false;
    }
    if (growthInvestments.some((line) => !line.fundingMonthKey)) {
      toast({
        title: 'Missing funding month',
        description: 'Select the requested funding month.',
        status: 'warning',
      });
      return false;
    }
    if (growthInvestments.some((line) => !isGrowthInvestmentComplete(line.growthInvestment))) {
      setGrowthInvestmentTouched(true);
      toast({
        title: 'Growth investment required',
        description: 'Enter the growth investment amount for each funding month.',
        status: 'warning',
      });
      return false;
    }
    return true;
  };

  const buildPreviewInvoice = () => {
    if (!selectedInvoice) {
      return null;
    }
    return {
      ...selectedInvoice,
      setOffEnabled: true,
      requestReceivedDate: growthInvestments[0]?.requestReceivedDate,
      fundingMonthKey: growthInvestments[0]?.fundingMonthKey,
      growthInvestment: sumGrowthInvestments(growthInvestments),
      growthInvestments: persistedGrowthInvestments(growthInvestments),
      setOffCreatedDate,
    };
  };

  const onDownloadPdf = async () => {
    if (!validateSetOffFields()) {
      return;
    }
    setDownloadingPdf(true);
    try {
      await downloadInvoicePdf(buildPreviewInvoice());
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to generate PDF',
        status: 'error',
      });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const onSaveSetOff = async () => {
    if (!selectedInvoiceId || !validateSetOffFields()) {
      return;
    }
    setSavingSetOff(true);
    try {
      const saved = await addAccountingInvoiceSetOffApi({
        invoiceId: selectedInvoiceId,
        setOff: {
          requestReceivedDate: growthInvestments[0]?.requestReceivedDate,
          fundingMonthKey: growthInvestments[0]?.fundingMonthKey,
          growthInvestment: sumGrowthInvestments(growthInvestments),
          growthInvestments: persistedGrowthInvestments(growthInvestments),
          setOffCreatedDate,
        },
      });
      toast({
        title: 'Set-off saved',
        description: `Set-off attached to ${saved.invoiceNumber}.`,
        status: 'success',
      });
      navigate(`/accounting-internal/${saved.id}`);
    } catch (error) {
      const message = error?.response?.data?.error || 'Failed to save set-off';
      toast({
        title: 'Error',
        description: message,
        status: 'error',
      });
    } finally {
      setSavingSetOff(false);
    }
  };

  const previewInvoice = buildPreviewInvoice();

  return (
    <ContentBox>
      <BackLink text="Back to invoices" path="/accounting-internal" />
      <Flex mt={6} gap={6} align="flex-start" direction={{ base: 'column', xl: 'row' }}>
        <Box flex="1 1 60%" minW={0}>
          <Card mb={6} position="relative">
            <Heading variant="sectionHeader" borderColor="twelve.green.300" mb={6}>
              Create set-off
            </Heading>
            {(loadingInvoices || loadingInvoice) && (
              <AbsoluteCenter bg="whiteAlpha.800" zIndex={1} borderRadius="inherit">
                <Spinner />
              </AbsoluteCenter>
            )}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={8}>
              <Box>
                <Text fontSize="sm" mb={2}>
                  Customer
                </Text>
                <SelectInput
                  options={customers}
                  value={selectedCustomerId}
                  onChange={onCustomerChange}
                  enableSearch
                  placeholder="Select customer"
                />
              </Box>
              <Box>
                <Text fontSize="sm" mb={2}>
                  Invoice
                </Text>
                <SelectInput
                  options={invoiceOptions}
                  value={selectedInvoiceId}
                  onChange={onInvoiceChange}
                  enableSearch
                  placeholder={
                    selectedCustomerId ? 'Select invoice without set-off' : 'Select customer first'
                  }
                  isDisabled={!selectedCustomerId || loadingInvoices || !invoiceOptions.length}
                />
                {selectedCustomerId && !loadingInvoices && !invoiceOptions.length ? (
                  <Text fontSize="xs" color="secondaryGray.600" mt={1}>
                    No invoices without a set-off for this customer.
                  </Text>
                ) : null}
              </Box>
              <Box>
                <Text fontSize="sm" mb={2}>
                  Set-off created date
                </Text>
                <Text fontWeight={700}>{moment(setOffCreatedDate).format('MMMM DD, YYYY')}</Text>
              </Box>
            </SimpleGrid>

            {selectedInvoice && (
              <>
                <SetOffFundingPanel
                  standalone
                  setOffEnabled
                  onSetOffEnabledChange={() => {}}
                  lines={growthInvestments}
                  onLineChange={onGrowthInvestmentLineChange}
                  onAddLine={onAddGrowthInvestment}
                  onRemoveLine={onRemoveGrowthInvestment}
                  loadingLineIds={loadingLineIds}
                  deal={latestDeal}
                  growthInvestmentInvalid={growthInvestmentTouched}
                  dueDate={selectedInvoice.dueDate}
                />

                <Flex justify="flex-end" gap={3} mt={8}>
                  <Button variant="outline" onClick={() => navigate('/accounting-internal')}>
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    isLoading={downloadingPdf}
                    onClick={onDownloadPdf}
                  >
                    Download PDF
                  </Button>
                  <Button variant="brand" onClick={onSaveSetOff} isLoading={savingSetOff}>
                    Save set-off
                  </Button>
                </Flex>
              </>
            )}
          </Card>
        </Box>

        <Box flex="1 1 40%" minW={{ base: '100%', xl: '420px' }} position="sticky" top="24px">
          {previewInvoice && (
            <InvoicePreview
              invoiceNumber={previewInvoice.invoiceNumber}
              invoiceDate={previewInvoice.invoiceDate}
              dueDate={previewInvoice.dueDate}
              billingDetails={previewInvoice.billingDetails}
              lines={previewInvoice.lines || []}
              setOffEnabled
              requestReceivedDate={growthInvestments[0]?.requestReceivedDate}
              fundingMonthKey={growthInvestments[0]?.fundingMonthKey}
              growthInvestment={sumGrowthInvestments(growthInvestments)}
              growthInvestments={growthInvestments}
              setOffCreatedDate={setOffCreatedDate}
              signature={previewInvoice.signature}
            />
          )}
        </Box>
      </Flex>
    </ContentBox>
  );
};

export default CreateSetOff;
